<!-- TODO: MAKE THIS A REUSABLE COMPONENT -->

<!-- Note: Do not change to v-if, most elements need to be rendered in DOM all the time -->
<template>
  <div v-show="props.currentImg && !uploadedImage && !props.showCharBrand">
    <div class="d-flex justify-content-start">
      <div class="d-flex flex-column gap-2">
        <!-- 
          Note: Most cases, props.currentImg will be updated  when cropping finishes,
          croppedImage is just used here to allow ColorTheif to work.
          As ColorThief needs a fully loaded image to work.
          -------------------------------------------------------------------
          e.g. BrandLogoUploadModal.vue

          Note: CrossOrigin is set to anonymous to allow ColorThief to work with images from different origins.
          -------------------------------------------------------------------
         -->
        <img
          :src="croppedImage || props.currentImg"
          :id="`croppedImage-${props.id}`"
          class="img-fluid preview-img cursor-pointer"
          @click="uploadNewImg()"
        />
        <template v-if="!props.btnMode">
          <span class="upload-text" @click="uploadNewImg()">
            Upload New Image
          </span>
        </template>
      </div>
    </div>
    <template v-if="props.btnMode">
      <div class="d-flex justify-content-start mt-2">
        <button class="btn btn-primary" @click="uploadNewImg()">
          Upload New Image
        </button>
      </div>
    </template>
  </div>
  <div v-show="!props.currentImg && !props.showCharBrand">
    <div
      v-if="!uploadedImage"
      @dragenter.self="handleHighlight"
      @dragleave.self="handleHighlight"
      @dragover.prevent
      @drop.prevent="handleDrop"
      class="browse-hover p-5 d-flex form-control justify-content-center text-center"
      :class="{
        'is-highlight': activeHighlight,
        'bg-white': !activeHighlight,
        'is-invalid': props.imageRequired,
      }"
      style="cursor: pointer; border-radius: 0.375rem"
      @click="fileInput.click()"
    >
      <div style="pointer-events: none">
        <UploadIcon class="w-100" />Drag & drop files or
        <label
          class="text-center"
          style="
            text-decoration: underline;
            cursor: pointer;
            color: #64738b;
            pointer-events: fill;
          "
          >Browse
        </label>
      </div>
      <input
        id="file"
        ref="fileInput"
        class="d-none"
        type="file"
        accept="image/*"
        @change="handleFileUpload"
      />
    </div>
  </div>
  <div v-show="uploadedImage">
    <div class="form-control d-flex justify-content-between">
      <div>{{ uploadedImage?.name }}</div>
      <ICO17 :id="`removeBtn-${props.id}`" style="cursor: pointer" />
    </div>
    <div class="d-flex justify-content-center mt-3">
      <img v-show="!croppedImage" :id="`image-${props.id}`" :src="image" />
    </div>
    <template v-if="!croppedImage">
      <input
        v-model="zoomRange"
        type="range"
        step="0.5"
        class="form-range w-100 mt-2"
        :id="`zoomRange-${props.id}`"
      />
      <div class="d-flex justify-content-end">
        <button
          :id="`cropBtn-${props.id}`"
          type="button"
          class="btn btn-primary mt-2"
        >
          Crop
        </button>
      </div>
    </template>
  </div>
  <div v-show="props.showCharBrand && !uploadedImage">
    <div class="d-flex flex-column gap-3 m-0 brand-upload-section">
      <div
        class="brand-image-container d-flex justify-content-center align-items-center"
      >
        <img
          v-if="props.currentImg"
          :src="croppedImage || props.currentImg"
          :id="`croppedImage-${props.id}`"
          class="brand-image img-fluid cursor-pointer"
          @click="uploadNewImg()"
        />
        <div
          v-else
          class="brand-placeholder d-flex justify-content-center align-items-center"
        >
          <div
            class="brand-initial-circle d-flex justify-content-center align-items-center"
          >
            {{ store.brand?.name.charAt(0) || "" }}
          </div>
        </div>
      </div>

      <button
        class="btn btn-primary d-flex justify-content-center align-items-center brand-upload-btn w-100"
        @click="uploadNewImg()"
      >
        Upload New Image
      </button>
    </div>
  </div>
</template>

<script setup>
import ICO17 from "@/assets/icons/ICO17.vue";
import UploadIcon from "@/pages/Marketing/Giveaway/CreateGiveaway/GiveawayItem/UploadIcon.vue";
import "cropperjs/dist/cropper.css";
import Cropper from "cropperjs";
import { computed, ref, watch } from "vue";
import { useBrandStore } from "@/stores/brand";

const store = useBrandStore();
const emits = defineEmits(["uploaded-image", "crop-mode"]);

const props = defineProps({
  // Note: Only pass id, if component is rendered mutliple times in the DOM
  id: {
    type: String,
    default: "1",
  },
  image: {
    type: [String, File],
    default: null,
  },
  aspectRatio: {
    type: Number,
    default: 1,
  },
  currentImg: {
    type: String,
    default: null,
  },
  // True if it's mandatory to upload an image, false if it's optional
  imageRequired: {
    type: Boolean,
    default: true,
  },
  btnMode: {
    type: Boolean,
    default: false,
  },
  /*
   * If croppedImage is used for eventListener, this prop should be set to true
   * else it's always false
   */
  useCroppedImage: {
    type: Boolean,
    default: false,
  },
  /*
   * Condition for Carousel Component
   * else it's always false
   */
  showCharBrand: {
    type: Boolean,
    default: false,
  },
  pixelLimit: {
    type: Object,
    required: false,
  },
});

const activeHighlight = ref(false);
const uploadedImage = ref(null);
const croppedImage = ref(null);
const fileInput = ref(null);
const zoomRange = ref(0);

const image = computed(() => {
  if (uploadedImage.value) {
    return URL.createObjectURL(uploadedImage.value);
  }
  return null;
});

watch(image, (newImg) => {
  if (newImg) {
    const imgElm = document.getElementById(`image-${props.id}`);
    const cropBtn = document.getElementById(`cropBtn-${props.id}`);
    const removeBtn = document.getElementById(`removeBtn-${props.id}`);
    const range = document.getElementById(`zoomRange-${props.id}`);

    var cropper = null;
    var cropperInitialized;
    var initalValue = 0;

    imgElm.addEventListener("load", () => {
      // Prevent duplicate initializations
      if (cropperInitialized) {
        return;
      }

      cropper = new Cropper(imgElm, {
        viewMode: 1,
        aspectRatio: props.aspectRatio,
        dragMode: "move",
        background: false,
        autoCropArea: 0.85,
        guides: false,
        center: true,
        cropBoxMovable: false,
        cropBoxResizable: false,
        toggleDragModeOnDblclick: false,
        minContainerHeight: 380,
        zoomOnWheel: false,
      });

      // Note: Emit parent component that it's currently in crop mode
      // Example uses: Hiding "Continue" button if user is in cropping mode
      emits("crop-mode", true);

      cropperInitialized = true;
    });

    imgElm.addEventListener("ready", () => {
      // Zoom out max
      imgElm.naturalWidth, imgElm.naturalHeight;

      if (imgElm.naturalWidth >= imgElm.naturalHeight) {
        // Note: Images with longer width don't need to add initalValue
        // This is to prevent images with weird aspect ratio to zoom in and out too much
        initalValue = 0;
      }

      if (cropper) {
        cropper.zoomTo(0);
      }
    });

    range.addEventListener("input", () => {
      if (!cropper) return;

      let zoomValue = Number(range.value);

      // Note: zoomValue is divided by 10 to make it more smoother,
      // + 2 is to compensate zooming out to 0 during initial image load, refer code above
      const formula = initalValue + zoomValue / 10;

      cropper.zoomTo(formula);
    });

    cropBtn.onclick = () => {
      const cropImg = document.getElementById(`croppedImage-${props.id}`);

      croppedImage.value = cropper
        .getCroppedCanvas({
          maxWidth: 2048,
          maxHeight: 2048,
        })
        .toDataURL(uploadedImage.value.type);

      emits("uploaded-image", {
        image: croppedImage.value,
        el: cropImg,
      });

      cropper.destroy();
      cropper = null;

      if (!props.useCroppedImage) croppedImage.value = null;
      uploadedImage.value = null;
      zoomRange.value = 0;

      emits("crop-mode", false);
    };

    removeBtn.onclick = () => {
      uploadedImage.value = null;

      if (!props.currentImg) {
        emits("uploaded-image", { image: null, el: null });
      }

      if (cropper) {
        cropper.destroy();
        cropper = null;

        emits("crop-mode", false);
      }
    };
  }
});

const uploadNewImg = () => {
  fileInput.value.click();
};

const handleHighlight = () => (activeHighlight.value = !activeHighlight.value);

const handleFileUpload = (event) => {
  let file = event.target.files[0];

  if (!file) {
    alert("Please upload an image file.");
    return;
  }

  if (!file.type.startsWith("image/")) {
    alert("Please upload an image file.");
    return;
  }

  if (file.size > 3 * 1024 * 1024) {
    alert("Please upload an image file with less than 3MB.");
    return;
  }

  if (props.pixelLimit) {
    const img = new Image();
    img.onload = () => {
      if (
        img.width < props.pixelLimit.width ||
        img.height < props.pixelLimit.height
      ) {
        alert(
          `Please upload an image file with minimum resolution of ${props.pixelLimit.width}x${props.pixelLimit.height}.`
        );
        return;
      }
      uploadedImage.value = file;
    };
    img.src = URL.createObjectURL(file);
    return;
  }

  uploadedImage.value = file;
};

const handleDrop = (event) => {
  handleHighlight();
  let files = event.dataTransfer.files;
  handleFileUpload({ target: { files } });
};

const removeCroppedImage = () => {
  croppedImage.value = null;
};

defineExpose({
  uploadNewImg,
  removeCroppedImage,
});
</script>

<style scoped>
.is-highlight {
  background: #e5e5e5;
}
.upload-text {
  text-decoration: underline;
  cursor: pointer;
  color: #ff0000;
  transition: color 0.2s ease;
}
.upload-text:hover {
  color: #cc0000;
}
.preview-img {
  border: 2px solid #e5e5e5;
  height: 240px;
  width: 360px;
  object-fit: cover;
  object-position: center;
  border-radius: 0.375rem;
}
.brand-image-container {
  position: relative;
  aspect-ratio: 17 / 10;
  width: 100%;
  max-width: 306px;
  border-radius: 0.375rem;
  overflow: hidden;
  background: #ecebeb;
  flex-shrink: 0;
}
.brand-image,
.brand-placeholder {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 0.375rem;
}
.brand-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
}
.brand-initial-circle {
  font-size: 5rem;
  height: 7rem;
  width: 7rem;
  border-radius: 50%;
  background: white;
}
.brand-upload-btn {
  max-width: 306px;
  flex: 1;
}
@media (max-width: 992px) {
  .brand-upload-section {
    flex-direction: column;
    align-items: center !important;
  }
  .brand-upload-btn {
    width: 100%;
  }
}

.brand-image-container {
  transition: transform 0.3s ease;
}

.brand-image-container:hover {
  transform: scale(1.05);
  cursor: pointer;
}
</style>
